// ============================================================================
// BESPOKIAN Health - Shipping Info Page
// Shipping details with luxury styling
// ============================================================================

import { Navigation } from '@/components/layout/Navigation';
import { Footer } from '@/components/layout/Footer';
import { Truck, Package, Clock, MapPin, CheckCircle2, Thermometer, Shield } from 'lucide-react';

const shippingOptions = [
  { icon: Truck, title: 'Free Standard Shipping', desc: 'Typically 2-3 business days on orders $50+', highlight: true },
  { icon: Package, title: 'Express Shipping', desc: 'Next-day delivery available for a flat fee' },
  { icon: Clock, title: 'Same-Day Pickup', desc: 'Coming soon to partner locations' },
];

export function ShippingPage() {
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navigation />

      <main className="flex-1 pt-24">
        {/* Hero */}
        <section className="bg-gradient-to-br from-navy-50 via-white to-gold-50/30 py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="w-16 h-0.5 bg-gold-500 mx-auto mb-6" />

            <p className="text-gold-600 font-medium tracking-wide uppercase text-sm mb-4">
              Delivery
            </p>

            <h1 className="font-display text-4xl md:text-5xl text-navy-800 tracking-tight leading-tight mb-6 italic">
              Fast, Free Shipping
            </h1>

            <p className="text-xl text-navy-600 max-w-2xl mx-auto">
              Your medications delivered safely to your door. We plan to offer free shipping on orders over $50.
            </p>
          </div>
        </section>

        {/* Shipping Options */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-3 gap-6 mb-16">
              {shippingOptions.map((item) => (
                <div
                  key={item.title}
                  className={`text-center p-8 rounded-xl border transition-shadow hover:shadow-card ${
                    item.highlight
                      ? 'bg-sage-50 border-sage-200'
                      : 'bg-navy-50 border-navy-100'
                  }`}
                >
                  <div className={`w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4 ${
                    item.highlight ? 'bg-sage-100' : 'bg-gold-100'
                  }`}>
                    <item.icon className={`h-8 w-8 ${item.highlight ? 'text-sage-600' : 'text-gold-600'}`} />
                  </div>
                  <h3 className="font-display text-xl text-navy-800 tracking-tight mb-2">{item.title}</h3>
                  <p className="text-navy-600">{item.desc}</p>
                  {item.highlight && (
                    <span className="inline-block mt-3 px-3 py-1 bg-sage-200 text-sage-700 text-xs font-semibold rounded-full">
                      Most Popular
                    </span>
                  )}
                </div>
              ))}
            </div>

            {/* Details Grid */}
            <div className="grid md:grid-cols-2 gap-8">
              {/* Where We Ship */}
              <div className="bg-navy-50 rounded-xl p-8 border border-navy-100">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-gold-100 rounded-lg flex items-center justify-center">
                    <MapPin className="h-5 w-5 text-gold-600" />
                  </div>
                  <h3 className="font-display text-xl text-navy-800 tracking-tight">Where We Ship</h3>
                </div>
                <p className="text-navy-600 mb-4">
                  We ship to all 50 US states. Where permitted by law, controlled substances cannot be shipped to P.O. boxes.
                </p>
                <ul className="space-y-3">
                  {['All 50 US states', 'US territories (standard shipping)', 'APO/FPO military addresses'].map((item) => (
                    <li key={item} className="flex items-center gap-3 text-navy-700">
                      <CheckCircle2 className="h-5 w-5 text-sage-600 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Packaging & Safety */}
              <div className="bg-navy-50 rounded-xl p-8 border border-navy-100">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-sage-100 rounded-lg flex items-center justify-center">
                    <Shield className="h-5 w-5 text-sage-600" />
                  </div>
                  <h3 className="font-display text-xl text-navy-800 tracking-tight">Packaging & Safety</h3>
                </div>
                <p className="text-navy-600 mb-4">
                  All medications ship in discreet, tamper-evident packaging. Cold-chain medications are shipped in validated, temperature-controlled packaging.
                </p>
                <ul className="space-y-3">
                  {[
                    'Discreet, unmarked packaging',
                    'Temperature-controlled when needed',
                    'Signature required for controlled substances',
                    'Real-time tracking on all orders'
                  ].map((item) => (
                    <li key={item} className="flex items-center gap-3 text-navy-700">
                      <CheckCircle2 className="h-5 w-5 text-sage-600 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Cold Chain Notice */}
            <div className="mt-8 bg-gold-50 rounded-xl p-6 border border-gold-200">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-gold-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Thermometer className="h-5 w-5 text-gold-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-navy-800 mb-1">Temperature-Sensitive Medications</h4>
                  <p className="text-navy-600 text-sm">
                    Medications requiring refrigeration (like insulin) are shipped with gel ice packs in validated insulated packaging 
                    at no extra charge to ensure clinical integrity. We recommend express shipping in warm weather.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}



